//import liraries
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Card, CardMedia, Chip, Stack, useTheme, useMediaQuery } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { useNavigate } from 'react-router-dom';
import CategoryChips from '../../Components/CategoryChips';
import { calculateDistance } from '../../Utils/googleMapsDistance';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Module-level cache to prevent duplicate distance calculations across component instances
const distanceCache = new Map<string, number>();

// Memoized PlaceCard component to prevent unnecessary re-renders
const PlaceCard = React.memo<{
  place: any;
  userLocation: { lat: number; lng: number } | null;
  navigate: (path: string) => void;
  getRatingColor: (rating: number) => string;
}>(({ place, userLocation, navigate, getRatingColor }) => {
  const [distance, setDistance] = useState<number | null>(null);
  const calculationRef = useRef<string | null>(null);
  
  const imageUrl = place?.files?.[0]?.fileURL || '';
  const rating = place?.averageRating ?? 0;
  const tags: string[] = place?.tags ?? [];
  const placeCategories: string[] = place?.categories ?? [];
  
  // Memoize coordinate values to avoid unnecessary recalculations
  const userLat = useMemo(() => userLocation?.lat, [userLocation?.lat]);
  const userLng = useMemo(() => userLocation?.lng, [userLocation?.lng]);
  const placeLat = useMemo(() => place?.location?.lat, [place?.location?.lat]);
  const placeLng = useMemo(() => place?.location?.lng, [place?.location?.lng]);
  
  // Create a unique cache key from coordinates
  const cacheKey = useMemo(() => {
    if (userLat && userLng && placeLat && placeLng) {
      return `${userLat},${userLng},${placeLat},${placeLng}`;
    }
    return null;
  }, [userLat, userLng, placeLat, placeLng]);
  
  useEffect(() => {
    if (!cacheKey) {
      setDistance(null);
      return;
    }
    
    // Check module-level cache first
    if (distanceCache.has(cacheKey)) {
      setDistance(distanceCache.get(cacheKey)!);
      calculationRef.current = cacheKey;
      return;
    }
    
    // Skip if already calculated for this exact coordinate set in this component instance
    if (calculationRef.current === cacheKey) return;
    
    calculationRef.current = cacheKey;
    const [lat1, lng1, lat2, lng2] = cacheKey.split(',').map(Number);
    
    calculateDistance(lat1, lng1, lat2, lng2)
      .then(result => {
        // Only update if this is still the current calculation
        if (calculationRef.current === cacheKey) {
          distanceCache.set(cacheKey, result);
          setDistance(result);
        }
      })
      .catch(() => {
        // Only update if this is still the current calculation
        if (calculationRef.current === cacheKey) {
          setDistance(null);
        }
      });
    
    // Cleanup function
    return () => {
      calculationRef.current = null;
    };
  }, [cacheKey]);

  return (
    <Card
      key={place._id}
      sx={{
        flex: '0 0 40%', // roughly two cards per viewport
        minWidth: 180,
        borderRadius: '24px',
        // boxShadow: 3,
        overflow: 'hidden',
      }}
      onClick={() => navigate(`/places/${place._id}`)}
    >
      {/* Image with tag overlays */}
      <Box sx={{ position: 'relative' }}>
        <CardMedia component="img" height="160" image={imageUrl} alt={place.name} />
        {tags?.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
            }}
          >
            {tags.slice(0, 2).map((tag, index) => (
              <Chip
              key={index}
              label={tag}
              size="small"
              sx={{
                bgcolor: 'rgba(236, 224, 218, 0.6)', // Muted beige-grey with transparency
                backdropFilter: 'blur(4px)',
                fontSize:'14px',
                fontWeight: 500,
                color: '#49454F', // Dark grey text
                borderRadius: '8px', // Rounded rectangular shape
                border: '1px solid rgb(218, 213, 223)',
                padding:'16px',
                '& .MuiChip-label': {
                  padding: '16px 8px',
                }
              }}
            />
            ))}
          </Stack>
        )}
        {/* Category at bottom left */}
        {placeCategories?.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
            }}
          >
            <Chip
              label={placeCategories[0]}
              size="small"
              sx={{
                bgcolor: 'rgba(0,0,0,0.1)',
                backdropFilter: 'blur(1px)',
                color: 'white',
                fontWeight: 500,
                border: '1px solid #CAC4D0',
                borderRadius: '6px',
              }}
            />
          </Box>
        )}
      </Box>

      {/* Details section */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'white',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
          <Typography 
            variant="subtitle1" 
            fontWeight="bold" 
            noWrap
          >
            {place.name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            {distance ? `${distance}km` : 'N/A'}
          </Typography>
        </Box>

        {/* Rating pill */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: getRatingColor(rating),
            color: 'white',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ lineHeight: 1 }}>
            {rating}
          </Typography>
          <StarIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>
    </Card>
  );
});

PlaceCard.displayName = 'PlaceCard';

// create a component
const DashboardPlaceScreen: React.FC<{ title: string ,noCategories?: boolean}> = ({ title,noCategories }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  
  // Get the new places array from the dashboard slice newPlaces
  const type = title !== 'Nearby places' ? 'newPlaces' : 'curatedPlaces';
  const newPlaces = useSelector((state: RootState) => state.dashboard[type]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const userLocation = useSelector((state: RootState) => state.location.location);
  const categories = useSelector((state: RootState) => state.dashboard.categories);

  const [modalOpen, setModalOpen] = useState(false);
  const [sortedPlaces, setSortedPlaces] = useState<any[]>([]);
  
  // Calculate maxInitialDisplay based on screen width: xs (3), sm (5), md+ (8)
  const maxInitialDisplay = isMd ? 8 : isXs ? 5 : 5;
  // Helper to decide colour of the rating pill (same logic as PlaceDetail)
  const getRatingColor = (rating: number) => {
    if (rating >= 4) return '#2e7d32'; // Green for 4+
    if (rating >= 3) return '#ed6c02'; // Orange for 3-3.9
    return '#d32f2f'; // Red for <3
  };

  // Calculate distances and sort places by distance (min to max) - only for "Nearby places"
  useEffect(() => {
    const isNearbyPlaces = title === 'Nearby places';
    
    // For non-nearby places, just use the places as-is without sorting
    if (!isNearbyPlaces) {
      setSortedPlaces(newPlaces || []);
      return;
    }

    const calculateDistancesAndSort = async () => {
      if (!userLocation || !newPlaces || newPlaces.length === 0) {
        setSortedPlaces(newPlaces || []);
        return;
      }

      try {
        const placesWithDistance = await Promise.all(
          newPlaces.map(async (place: any) => {
            const placeLat =
              place.location?.coordinates && place.location.coordinates.length >= 2
                ? place.location.coordinates[1]
                : place.location?.lat;
            const placeLng =
              place.location?.coordinates && place.location.coordinates.length >= 2
                ? place.location.coordinates[0]
                : place.location?.lng;

            if (placeLat == null || placeLng == null) {
              return {
                ...place,
                distanceValue: Infinity,
              };
            }

            try {
              const distance = await calculateDistance(
                userLocation.lat,
                userLocation.lng,
                placeLat,
                placeLng
              );
              return {
                ...place,
                distanceValue: distance,
              };
            } catch (error) {
              console.warn(`Error calculating distance for place ${place.name}:`, error);
              return {
                ...place,
                distanceValue: Infinity,
              };
            }
          })
        );

        // Sort places by distance (min to max)
        const sorted = placesWithDistance.sort(
          (a, b) => (a.distanceValue || Infinity) - (b.distanceValue || Infinity)
        );

        setSortedPlaces(sorted);
      } catch (error) {
        console.error('Error calculating distances:', error);
        setSortedPlaces(newPlaces || []);
      }
    };

    calculateDistancesAndSort();
  }, [userLocation, newPlaces, title]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'row', mb: 1 }}>
      <Box onClick={() => {
        const sortBy = title === 'Nearby places' ? 'nearby' : 'newly_added';
        navigate('/allplaces', { state: { sortBy } });
      }} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography  fontWeight={500} sx={{fontSize: '16px', cursor: 'pointer', color: '#1D1B20'}}>{title}</Typography>
        <ArrowForwardIosIcon sx={{ fontSize: 14, color: '#1D1B20', ml: 1 }} />
      </Box>
        {sortedPlaces?.length > 4 && !noCategories && (
          <Typography
            sx={{ ml: 'auto', cursor: 'pointer' }}
            onClick={() => setModalOpen(!modalOpen)}
          >
            View All
          </Typography>
        )}
      </Box>
      {!noCategories && (
        <CategoryChips
        noTitle
        isSeeMoreVisibel={false}
        categories={categories}
        selectedCategories={selectedCategories}
        onChange={(newCategories) => {
          setSelectedCategories(newCategories.map(category => category.name));
        }}
        onChipClick={(categoryName, isSelected) => {
          if (isSelected) {
            navigate('/allplaces', { state: { category: categoryName } });
          }
        }}
          maxInitialDisplay={maxInitialDisplay}
          openModal={modalOpen}
          onModalClose={() => setModalOpen(false)}
        />
      )}

      {/* Horizontal scroll list */}
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          gap: 2,
          pb: 2,
          mt: 2,
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari and Opera
          },
        }}
      >
        {sortedPlaces?.map((place: any) => (
          <PlaceCard
            key={place._id}
            place={place}
            userLocation={userLocation}
            navigate={navigate}
            getRatingColor={getRatingColor}
          />
        ))}
      </Box>
    </Box>
  );
};

//make this component available to the app
export default DashboardPlaceScreen;
